import { createRouter } from "@tanstack/solid-router";
import { rootRoute } from "@/routes/__root";
import { NotFoundRoute } from "@/components/not-found-route";
import { authRoute } from "@/routes/_auth/route";
import { loginRoute } from "@/routes/_auth/login";
import { registerRoute } from "@/routes/_auth/register";
import { forgotPasswordRoute } from "@/routes/_auth/forgot-password";
import { resetPasswordRoute } from "@/routes/_auth/reset-password";
import { verifyEmailRoute } from "@/routes/_auth/verify-email";
import { socialCallbackRoute } from "@/routes/_auth/social-callback";
import { appRoute } from "@/routes/_app/route";
import { indexRoute } from "@/routes/_app/index";
import { profileRoute } from "@/routes/_app/profile";
import { debugRoute } from "@/routes/_app/debug";
import { organizationsRoute } from "@/routes/_app/organizations";
import { organizationDetailRoute } from "@/routes/_app/organizations.$orgId";
import { invitationsRoute } from "@/routes/_app/invitations";
import { themeGalleryRoute } from "@/routes/_app/themes";
import { languageRoute } from "@/routes/_app/language";
import { profileFontsRoute } from "@/routes/_app/profile.fonts";
import { profileShortcutsRoute } from "@/routes/_app/profile.shortcuts";
import { profileDeleteAccountRoute } from "@/routes/_app/profile.delete-account";
import { profileTransferSendRoute } from "@/routes/_app/profile.transfer-send";
import { profileTransferReceiveRoute } from "@/routes/_app/profile.transfer-receive";
import { menuRoute } from "@/routes/_app/menu";
import { posRoute } from "@/routes/_app/pos";
import { floorRoute } from "@/routes/_app/floor";
import { cashboxRoute } from "@/routes/_app/cashbox";
import { kdsRoute } from "@/routes/_app/kds";
import { inventoryRoute } from "@/routes/_app/inventory";
import { purchasingRoute } from "@/routes/_app/purchasing";
import { staffRoute } from "@/routes/_app/staff";
import { reportsRoute } from "@/routes/_app/reports";
import { customersRoute } from "@/routes/_app/customers";
import { reservationsRoute } from "@/routes/_app/reservations";
import { pluginRoutes } from "@/composition";
import { achievementsRoute } from "@/routes/_app/achievements";
import { trashRoute } from "@/routes/_app/trash";

const routeTree = rootRoute.addChildren([
  socialCallbackRoute,
  authRoute.addChildren([
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
  ]),
  appRoute.addChildren([
    indexRoute, // path: "/" — Dashboard
    posRoute, // path: "/pos" — Point of sale
    kdsRoute, // path: "/kds" — Kitchen display
    menuRoute, // path: "/menu" — Menu management
    inventoryRoute, // path: "/inventory" — Ingredients + recipes
    purchasingRoute, // path: "/purchasing" — Suppliers + purchase orders (F3)
    reportsRoute, // path: "/reports" — Sales dashboard (F3)
    staffRoute, // path: "/staff" — Employees + PIN (F3)
    customersRoute, // path: "/customers" — Customers + loyalty + promos (F4)
    reservationsRoute, // path: "/reservations" — Reservations + waitlist (F4)
    floorRoute, // path: "/floor" — Tables / floor
    cashboxRoute, // path: "/cashbox" — Register sessions
    ...pluginRoutes, // path: "/activity" — ACTIVITY-CORE feed L3 (+ vista por turno)
    achievementsRoute, // path: "/achievements" — GAMIFICATION-CORE (XP/racha/badges)
    trashRoute, // path: "/trash" — ACTIVITY-CORE papelera L2 (catálogo)
    organizationsRoute,
    organizationDetailRoute,
    invitationsRoute,
    themeGalleryRoute,
    languageRoute,
    profileRoute,
    profileFontsRoute,
    profileShortcutsRoute,
    profileDeleteAccountRoute,
    profileTransferSendRoute,
    profileTransferReceiveRoute,
    debugRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  // URL desconocida → estado "no encontrado" premium (nunca pantalla en blanco),
  // rendered inside root <Outlet> with all providers available.
  defaultNotFoundComponent: NotFoundRoute,
  // "intent" preloads only when the user expresses intent (hover/focus on a
  // <Link>). The previous "viewport" setting eagerly fired `beforeLoad` and
  // PowerSync `resolveTables` EXPLAIN queries for ~20 sidebar routes the
  // moment the dashboard mounted. Combined with PowerSync's initial-sync
  // batches (each one re-invalidating subscribed queries), this saturated
  // the main thread for several seconds after login. Hover-only is the
  // standard tradeoff: a tiny first-hover latency in exchange for a calm
  // post-login render.
  defaultPreload: "intent",
  // oxlint-disable-next-line eslint(no-magic-numbers)
  defaultPreloadStaleTime: 30_000, // 30 s — don't re-preload the same route within 30 s
});

declare module "@tanstack/solid-router" {
  interface Register {
    router: typeof router;
  }
}
